import { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import cookie from 'react-cookies';
import jwtDecode from 'jwt-decode';
import AuthLayout from '../components/layout/authLayout';
import mlLogo from '../assets/images/mlLogo.svg';
import { loginRequest } from '../authConfig';
import MicrosoftLoginButton from '../components/layout/microsoftLoginButtonComp';
import { AppContext } from '../context/appContext';
import { LOCAL_STORAGE_KEYS, LOCAL_STORAGE_VALUES } from '../utils/constants';
import getApiBaseUrl from '../services/api/baseUrl';

const IS_DEV_LOGIN_ENABLED =
  process.env.REACT_APP_ENABLE_DEV_LOGIN === 'true' &&
  process.env.REACT_APP_ENV !== 'production';

const Login = () => {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const { setLoading, setProfile } = useContext(AppContext);
  const tenantHostHeader =
    typeof window !== 'undefined' && window.location
      ? { 'x-tenant-host': window.location.host }
      : {};

  const [devUsers, setDevUsers] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [devLoginLoading, setDevLoginLoading] = useState(false);
  const [devLoginError, setDevLoginError] = useState(null);

  useEffect(() => {
    if (!IS_DEV_LOGIN_ENABLED) return;
    axios
      .get(`${getApiBaseUrl()}auth/dev-users`, {
        headers: tenantHostHeader,
      })
      .then((r) => setDevUsers(r.data?.users || []))
      .catch(() => {});
  }, []);

  const handleLoginRedirect = async () => {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.LOGIN_IN_PROCESS,
      LOCAL_STORAGE_VALUES.LOGIN_IN_PROCESS,
    );
    setLoading(true);
    await instance.loginRedirect(loginRequest);
  };

  const handleDevLogin = async () => {
    if (!selectedEmail) return;
    setDevLoginLoading(true);
    setDevLoginError(null);
    try {
      const res = await axios.post(
        `${getApiBaseUrl()}auth/dev-login`,
        {
          email: selectedEmail,
        },
        {
          headers: tenantHostHeader,
        },
      );
      if (res.data?.token) {
        const jwtToken = res.data.token;
        cookie.save(
          'token',
          { token: jwtToken },
          {
            path: '/',
            maxAge: 60 * 60 * 8,
          },
        );
        setProfile({
          ...jwtDecode(jwtToken),
          token: jwtToken,
        });
        navigate('/', { replace: true });
      } else {
        setDevLoginError('Dev login returned no token');
      }
    } catch (err) {
      setDevLoginError(err.response?.data?.error || 'Dev login failed');
    } finally {
      setDevLoginLoading(false);
    }
  };

  return (
    <AuthLayout>
      <main className="loginPage flex-grow-1 d-flex align-items-center justify-content-center">
        <div className="logInContent text-black text-center">
          <div className="logInContentHead d-flex flex-column">
            <span>
              <img src={mlLogo} alt="mobileLIVE logo" />
            </span>
            <h2 className="mb-0">
              Let’s Get Started Position Requisition Form Portal
            </h2>
            <p className="mb-0">Sign in to continue to PRF</p>
          </div>
          <div
            className="btn-google bg-white"
            data-testid="microsoft-login-button"
          >
            <MicrosoftLoginButton
              buttonTheme={'light'}
              onClick={handleLoginRedirect}
            />
          </div>

          {IS_DEV_LOGIN_ENABLED && (
            <div
              className="mt-4 p-3 border rounded text-start"
              style={{ background: '#fff8e1', borderColor: '#ffc107' }}
            >
              <p className="mb-2 fw-bold" style={{ color: '#856404' }}>
                Dev Login (non-production only)
              </p>
              <select
                className="form-select form-select-sm mb-2"
                value={selectedEmail}
                onChange={(e) => setSelectedEmail(e.target.value)}
              >
                <option value="">— select a user —</option>
                {devUsers.map((u) => (
                  <option key={u.id} value={u.email}>
                    {u.email} [{u.roles?.[0]?.role ?? 'no role'}] (
                    {u.tenant?.subdomain ?? u.tenant_id})
                  </option>
                ))}
              </select>
              <button
                className="btn btn-warning btn-sm w-100"
                onClick={handleDevLogin}
                disabled={!selectedEmail || devLoginLoading}
              >
                {devLoginLoading ? 'Logging in…' : 'Login as selected user'}
              </button>
              {devLoginError && (
                <p className="text-danger mt-1 mb-0 small">{devLoginError}</p>
              )}
              {selectedEmail && (
                <p className="text-muted mt-1 mb-0 small">
                  Tenant:{' '}
                  <strong>
                    {devUsers.find((u) => u.email === selectedEmail)?.tenant
                      ?.subdomain ?? '?'}
                  </strong>
                  {' — for full tenant isolation test, access via '}
                  <code>
                    {devUsers.find((u) => u.email === selectedEmail)?.tenant
                      ?.subdomain ?? '?'}
                    .localhost:3000
                  </code>
                </p>
              )}
            </div>
          )}

          <p className="mb-0 mt-3">
            mobileLIVE Inc &copy; {new Date().getFullYear()}
          </p>
        </div>
      </main>
    </AuthLayout>
  );
};

export default Login;
